import { Button, DropdownSelect } from '@components/common';
import type { DropdownSelectOption } from '@components/common/DropdownSelect';
import { DatePicker } from '@components/common/DatePicker';
import { Download } from 'lucide-react';
import React from 'react';
import type {
  InventorySupplierInvoiceStatus,
  InventorySupplierRecord,
} from '@/renderer/types/inventory';

interface SupplierInvoicesToolbarProps {
  supplierFilter: string;
  statusFilter: InventorySupplierInvoiceStatus | 'all';
  dateFrom: string;
  dateTo: string;
  itemFilterIds: string[];
  suppliers: InventorySupplierRecord[];
  itemOptions: DropdownSelectOption[];
  itemOptionsLoading?: boolean;
  itemOptionsHasMore?: boolean;
  isExporting?: boolean;
  canExport?: boolean;
  onSupplierChange: (value: string) => void;
  onStatusChange: (value: InventorySupplierInvoiceStatus | 'all') => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onItemFilterChange: (value: string[]) => void;
  onItemSearchChange: (value: string) => void;
  onLoadMoreItems: () => void;
  onExportCsv: () => void;
  onCreate: () => void;
}

export function SupplierInvoicesToolbar({
  supplierFilter,
  statusFilter,
  dateFrom,
  dateTo,
  itemFilterIds,
  suppliers,
  itemOptions,
  itemOptionsLoading = false,
  itemOptionsHasMore = false,
  isExporting = false,
  canExport = false,
  onSupplierChange,
  onStatusChange,
  onDateFromChange,
  onDateToChange,
  onItemFilterChange,
  onItemSearchChange,
  onLoadMoreItems,
  onExportCsv,
  onCreate,
}: SupplierInvoicesToolbarProps) {
  const supplierOptions = [
    { value: 'all', label: 'All suppliers' },
    ...suppliers.map((s) => ({ value: s.id, label: s.name })),
  ];
  const itemDisplayLabel = itemFilterIds.length
    ? `${itemFilterIds.length} item${itemFilterIds.length === 1 ? '' : 's'} selected`
    : undefined;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Purchase Invoices
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Record supplier invoices, post them to update stock, and track
            outstanding payables.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={onExportCsv}
            isLoading={isExporting}
            disabled={!canExport}
            className="min-w-[140px]"
          >
            {!isExporting && <Download size={16} />}
            Export CSV
          </Button>
          <Button onClick={onCreate}>Create invoice</Button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <DropdownSelect
          label="Supplier"
          value={supplierFilter}
          options={supplierOptions}
          onChange={(v) => onSupplierChange(String(v))}
        />
        <DropdownSelect
          label="Status"
          value={statusFilter}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'draft', label: 'Draft' },
            { value: 'posted', label: 'Posted' },
            { value: 'partially_paid', label: 'Partially paid' },
            { value: 'paid', label: 'Paid' },
            { value: 'voided', label: 'Voided' },
          ]}
          onChange={(v) =>
            onStatusChange(v as InventorySupplierInvoiceStatus | 'all')
          }
        />
        <DropdownSelect
          label="Item"
          values={itemFilterIds}
          options={itemOptions}
          placeholder="All inventory items"
          customDisplayLabel={itemDisplayLabel}
          multiple
          searchable
          searchPlaceholder="Search items"
          onSearchChange={onItemSearchChange}
          hasMoreOptions={itemOptionsHasMore}
          isLoadingOptions={itemOptionsLoading}
          onLoadMoreOptions={onLoadMoreItems}
          emptyStateLabel="No items found."
          onMultiChange={onItemFilterChange}
        />
        <div className="grid grid-cols-2 gap-2">
          <DatePicker
            mode="single"
            label="From"
            value={dateFrom}
            onChange={onDateFromChange}
          />
          <DatePicker
            mode="single"
            label="To"
            value={dateTo}
            onChange={onDateToChange}
          />
        </div>
      </div>
    </div>
  );
}
